import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { play } from '../../hooks/useSound';

const defaultStyles = {
  Button: {
    cursor: 'pointer',
    border: '0',
    boxSizing: 'border-box',
    borderRadius: '6px',
    fontSize: '16px',
    fontFamily: 'Poppins',
    fontWeight: 500,
    lineHeight: '19px',
    outline: 'none',
    display: 'inline-block',
  },
};

const Button = ({
  mb = '3',
  to = null,
  type = 'button',
  disabled = false,
  theme = 'primary',
  className='',
  align='center',
  key,
  onClick,
  children,
  p = '2',
  styles = {width: '100%'},
  ariaExpanded = false,
  dataBsToggle = null,
  silent = false,
}) => {
  const mergedStyles = {
    ...defaultStyles.Button,
    ...(styles || {}) // Ensures user-defined styles override defaults
  };

  React.useEffect(() => {
    if (dataBsToggle !== 'dropdown' || typeof document === 'undefined') return;

    const closeFallbackDropdowns = (event) => {
      if (typeof window !== 'undefined' && window.bootstrap) return;

      const dropdownItem = event.target.closest('.dropdown-menu .dropdown-item');
      if (!dropdownItem && event.target.closest('.dropdown')) return;

      document.querySelectorAll('.dropdown-menu.show').forEach((menu) => {
        menu.classList.remove('show');
        menu.previousElementSibling?.setAttribute('aria-expanded', 'false');
      });
    };

    document.addEventListener('click', closeFallbackDropdowns);
    return () => document.removeEventListener('click', closeFallbackDropdowns);
  }, [dataBsToggle]);

  if (disabled) {
    mergedStyles.opacity = 0.5;
    mergedStyles.cursor = 'not-allowed';
    mergedStyles.pointerEvents = 'none';
  }

  const buttonClassName = ` 
    mb-${mb}
    p-${p} 
    btn-${theme} 
    ${className} 
    text-${align}`;

  const handleClick = (event) => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    if (!silent) play('tap');

    if (dataBsToggle === 'dropdown' && typeof window !== 'undefined' && !window.bootstrap) {
      const menu = event.currentTarget.nextElementSibling;

      if (menu?.classList.contains('dropdown-menu')) {
        const shouldOpen = !menu.classList.contains('show');

        document.querySelectorAll('.dropdown-menu.show').forEach((openMenu) => {
          if (openMenu !== menu) {
            openMenu.classList.remove('show');
            openMenu.previousElementSibling?.setAttribute('aria-expanded', 'false');
          }
        });

        menu.classList.toggle('show', shouldOpen);
        event.currentTarget.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      }
    }

    if (onClick) onClick(event);
  };

  const buttonElement = (
    <button
      style={mergedStyles}
      className={buttonClassName}
      key={key}
      onClick={handleClick}
      type={type}
      disabled={disabled}
      aria-expanded={ariaExpanded}
      data-bs-toggle={dataBsToggle}
    >
      {children}
    </button>
  );

  return to && !disabled ? (
    <Link
      to={to}
      className={`link ${buttonClassName}`}
      style={mergedStyles}
      onClick={handleClick}
      aria-expanded={ariaExpanded}
      data-bs-toggle={dataBsToggle}
    >
      {children}
    </Link>
  ) : (
    buttonElement
  );
};

Button.propTypes = {
  styles: PropTypes.object,
  key: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
  align: PropTypes.string,
  p: PropTypes.string,
  mb: PropTypes.string,
  to: PropTypes.string,
  children: PropTypes.node,
  ariaExpanded: PropTypes.bool,
  dataBsToggle: PropTypes.string,
  silent: PropTypes.bool,
};

export default Button;
